import {
  addMonths,
  amortize,
  contractBalanceOn,
  contractSchedule,
  formatDateLong,
  formatMonthLong,
  monthOf,
  parseAmount,
  type AccountDto,
  type InterestMethod,
  type LoanDto,
} from '@koverta/shared';
import { useMemo, useState } from 'react';
import { useToast } from '../components/toast';
import { Btn, Icon, MoneyInput, Seg } from '../components/ui';
import { del, errorMessage, put } from '../lib/api';
import { useHousehold, type Fmt } from '../lib/household';
import { fmtPercent, parsePercent } from '../lib/loan';
import { useLoans } from '../lib/queries';
import { EditLoanDialog } from './EditLoanDialog';
import { LoanChart, RepaymentTable } from './LoanChart';
import '../styles/loans.css';

const METHODS: [InterestMethod, string][] = [
  ['30_360', 'Monthly (30/360)'],
  ['actual_365', 'Daily (actual/365)'],
];

const payoff = (date: string | null) => (date ? formatMonthLong(monthOf(date)) : 'Never at this installment');

/** Loan panel (loans.md §4): what is owed, progress, payoff projection, what-if, rates and terms. */
export function LoanPanel({ account }: { account: AccountDto }) {
  const { fmt } = useHousehold();
  const q = useLoans();
  const loan = q.data?.find((l) => l.accountId === account.id);
  if (!loan) return null;
  return <LoanCard loan={loan} fmt={fmt} />;
}

function LoanCard({ loan, fmt }: { loan: LoanDto; fmt: Fmt }) {
  const { today } = useHousehold();
  const { toast } = useToast();
  const cur = loan.currency;
  const money = (v: number) => fmt.money(v, cur, cur === 'EUR' ? 2 : 0);
  const [extra, setExtra] = useState('');
  const [oneOff, setOneOff] = useState('');
  const [editing, setEditing] = useState(false);
  const [addingRate, setAddingRate] = useState(false);
  const [view, setView] = useState<'remaining' | 'whole'>('remaining');
  const [showPlan, setShowPlan] = useState(false);

  const extraMinor = parseAmount(extra, fmt.locale) ?? 0;
  const oneOffMinor = parseAmount(oneOff, fmt.locale) ?? 0;
  const contract = useMemo(
    () => ({
      originalPrincipal: loan.originalPrincipal,
      startDate: loan.startDate,
      paymentDay: loan.paymentDay,
      installment: loan.installment,
      rates: loan.rates,
      method: loan.interestMethod,
    }),
    [loan],
  );
  // The plan from what is owed now, and the same with the what-if payments; the contract plan for the whole loan.
  const { remaining, alt, whole } = useMemo(() => {
    const input = {
      balance: loan.balance,
      rates: loan.rates,
      installment: loan.installment,
      method: loan.interestMethod,
      firstPayment:
        loan.nextPayment ?? `${addMonths(monthOf(today), 1)}-${String(loan.paymentDay).padStart(2, '0')}`,
    };
    const base = loan.balance > 0 ? amortize(input) : null;
    const extraOn = extraMinor > 0 || oneOffMinor > 0;
    const withExtra =
      base && extraOn
        ? amortize({
            ...input,
            extraMonthly: Math.max(0, extraMinor),
            oneOff: oneOffMinor > 0 ? { date: today, amount: oneOffMinor } : null,
          })
        : null;
    return { remaining: base, alt: withExtra, whole: contractSchedule(contract) };
  }, [loan, contract, extraMinor, oneOffMinor, today]);
  const result =
    remaining && alt
      ? {
          payoffDate: alt.payoffDate,
          monthsSaved: remaining.payoffDate && alt.payoffDate ? remaining.rows.length - alt.rows.length : 0,
          interestSaved:
            remaining.payoffDate && alt.payoffDate ? remaining.totalInterest - alt.totalInterest : 0,
        }
      : null;
  // When the app's balance and the contract plan disagree, say so: usually the balance or a term is off.
  const byContract = contractBalanceOn(contract, today);
  const drift = byContract.after ? loan.balance - byContract.balance : 0;
  const drifts = Math.abs(drift) > Math.max(100, loan.originalPrincipal * 0.005);
  const chartRows = view === 'whole' ? whole.rows : (remaining?.rows ?? []);

  const paid = Math.round(loan.paidOff * 100);
  return (
    <section className="card loan" aria-label={`Loan ${loan.name}`} data-testid="loan-panel">
      <div className="loan-top">
        <div className="loan-k">
          <small>Still owed</small>
          <b data-testid="loan-balance">{money(loan.balance)}</b>
          <span>
            of {money(loan.originalPrincipal)} borrowed {formatDateLong(loan.startDate, fmt.locale)}
          </span>
        </div>
        <div className="loan-k">
          <small>Interest rate</small>
          <b>{fmtPercent(loan.currentRate, fmt)}</b>
          <span>
            {loan.effectiveRate !== null ? `effective ${fmtPercent(loan.effectiveRate, fmt)} · ` : ''}
            {METHODS.find(([m]) => m === loan.interestMethod)?.[1]}
          </span>
        </div>
        <div className="loan-k">
          <small>Installment</small>
          <b>{money(loan.installment)}</b>
          <span>
            {loan.nextPayment ? `next ${fmt.date(loan.nextPayment)}` : 'no payment scheduled'}
            {loan.fromAccountName ? ` · from ${loan.fromAccountName}` : ''}
          </span>
        </div>
        <div className="loan-k">
          <small>Paid off by</small>
          <b data-testid="loan-payoff">{payoff(loan.projection.payoffDate)}</b>
          <span>
            {loan.projection.months} payments · {money(loan.projection.totalInterest)} interest to go
            {loan.lastPayment ? ` · contract ends ${formatMonthLong(monthOf(loan.lastPayment))}` : ''}
          </span>
        </div>
      </div>

      <div className="loan-prog">
        <div
          className="bar"
          role="progressbar"
          aria-label="Paid off"
          aria-valuenow={paid}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <i style={{ width: `${Math.max(1, paid)}%` }} />
        </div>
        <div className="lbl">
          <span>{paid} % paid off</span>
          <span>
            {money(loan.interestPaid)} interest paid so far
            {loan.categoryName ? ` · budgeted in ${loan.categoryName}` : ''}
          </span>
        </div>
      </div>

      {drifts ? (
        <div className="banner warn loan-drift" role="status">
          The contract plan says {money(byContract.balance)} is owed after the payment on{' '}
          {formatDateLong(byContract.after!, fmt.locale)}; Ovce na broju has {money(loan.balance)} (
          {fmt.money(drift, cur, cur === 'EUR' ? 2 : 0, 'always')}). Check the terms or set what is owed in{' '}
          <button className="link" onClick={() => setEditing(true)}>
            Edit loan
          </button>
          .
        </div>
      ) : null}

      <div className="loan-chart-h">
        <div className="h4" style={{ margin: 0 }}>
          {view === 'whole' ? 'Whole loan, by the contract' : 'Planned payoff from today'}
        </div>
        <Seg
          value={view}
          onChange={setView}
          label="Chart range"
          options={[
            ['remaining', 'From today'],
            ['whole', 'Whole loan'],
          ]}
        />
      </div>
      {chartRows.length ? (
        <LoanChart
          rows={chartRows}
          alt={view === 'remaining' ? (alt?.rows ?? null) : null}
          start={view === 'whole' ? loan.originalPrincipal : loan.balance}
          today={today}
          currency={cur}
          fmt={fmt}
          title={
            view === 'whole'
              ? `Repayment of ${money(loan.originalPrincipal)} from ${formatDateLong(loan.startDate, fmt.locale)}, paid off ${payoff(whole.payoffDate)}`
              : `Still owed ${money(loan.balance)}, paid off ${payoff(remaining?.payoffDate ?? null)}`
          }
        />
      ) : (
        <div className="muted">Nothing left to pay.</div>
      )}
      {chartRows.length ? (
        <div>
          <button className="link" onClick={() => setShowPlan((v) => !v)} aria-expanded={showPlan}>
            {showPlan ? 'Hide' : 'Show'} the repayment plan ({chartRows.length} payments)
          </button>
          {showPlan ? <RepaymentTable rows={chartRows} today={today} currency={cur} fmt={fmt} /> : null}
        </div>
      ) : null}

      <div className="loan-row">
        <label className="field">
          Pay extra per month
          <MoneyInput
            className="input r"

            value={extra}
            onChange={setExtra}
            aria-label="Pay extra per month"
          />
        </label>
        <label className="field">
          One-off payment
          <MoneyInput
            className="input r"

            value={oneOff}
            onChange={setOneOff}
            aria-label="One-off payment"
          />
        </label>
        <div className="out" data-testid="loan-whatif" aria-live="polite">
          {result ? (
            result.payoffDate ? (
              <>
                Paid off by <b>{payoff(result.payoffDate)}</b> · {result.monthsSaved} months sooner · saves{' '}
                <b>{money(result.interestSaved)}</b> interest
              </>
            ) : (
              'This still never pays the loan off.'
            )
          ) : (
            <span className="muted">What if you paid more? It's a projection; nothing is scheduled.</span>
          )}
        </div>
      </div>

      <div className="loan-row" style={{ alignItems: 'flex-start' }}>
        <div className="loan-rates" style={{ flex: 1, minWidth: 220 }}>
          <div className="h4" style={{ margin: 0 }}>
            Interest rates
          </div>
          {loan.rates.map((r) => (
            <div className="r" key={r.effectiveFrom}>
              <b>{fmtPercent(r.annualRate, fmt)}</b>
              <span>from {formatDateLong(r.effectiveFrom, fmt.locale)}</span>
              {loan.rates.length > 1 ? (
                <button
                  className="ib"
                  aria-label={`Delete the rate from ${r.effectiveFrom}`}
                  onClick={async () => {
                    try {
                      await del(`/api/loans/${loan.accountId}/rates/${r.effectiveFrom}`);
                    } catch (e) {
                      toast(errorMessage(e));
                    }
                  }}
                >
                  <Icon name="x" size={14} />
                </button>
              ) : null}
            </div>
          ))}
          {addingRate ? (
            <RateForm loan={loan} fmt={fmt} onDone={() => setAddingRate(false)} />
          ) : (
            <div>
              <Btn variant="ghost" icon="plus" onClick={() => setAddingRate(true)}>
                Add rate change
              </Btn>
            </div>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <Btn variant="ghost" icon="edit" onClick={() => setEditing(true)}>
            Edit loan
          </Btn>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
            Amount borrowed, dates, term, rates, installment and what is still owed.
          </div>
        </div>
      </div>
      {editing ? <EditLoanDialog loan={loan} onClose={() => setEditing(false)} /> : null}
      <div className="loan-note">
        Interest is entered on each payment day as an unapproved transaction. Check it against the bank's
        repayment plan (plan otplate) and correct it before approving.
      </div>
    </section>
  );
}

function RateForm({ loan, fmt, onDone }: { loan: LoanDto; fmt: Fmt; onDone: () => void }) {
  const { today } = useHousehold();
  const [date, setDate] = useState(today);
  const [rate, setRate] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    const annualRate = parsePercent(rate, fmt);
    if (annualRate === null) return setErr('Enter the rate, e.g. 6,25');
    try {
      await put(`/api/loans/${loan.accountId}/rates`, { effectiveFrom: date, annualRate });
      onDone();
    } catch (e) {
      setErr(errorMessage(e));
    }
  };
  return (
    <div className="row-gap" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <label className="field">
        From
        <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </label>
      <label className="field">
        Rate (%)
        <input
          className="input r"
          inputMode="decimal"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          placeholder={fmt.rate(loan.currentRate)}
          aria-label="New interest rate"
        />
      </label>
      <Btn variant="primary" onClick={save}>
        Save rate
      </Btn>
      <Btn variant="ghost" onClick={onDone}>
        Cancel
      </Btn>
      {err ? <div className="err-t">{err}</div> : null}
    </div>
  );
}
